import type { MailboxChannelOut } from '@biasafe.models';

/**
 * What the mailbox card should say about a connected channel, in plain words.
 *
 * WHY THIS EXISTS. The card used to read only `status === 'REVOKED'` and
 * `connected`, so a channel the API reported as ERROR on every sync showed
 * "Connected. The first read happens on the next sweep." On staging
 * (2026-10-01) that was a mailbox whose `last_error` read
 * "HTTPStatusError: Client error '403 Forbidden' for url ...", and later
 * "RuntimeError: Google says the grant was revoked. Reconnect the mailbox."
 *
 * THE RAW ERROR IS NEVER SHOWN. It is a Python exception rendered as a
 * string: an exception type, then a URL with its query string, which names
 * API internals and can carry identifiers. Known failures map to a sentence
 * that says what to do; anything else gets a generic sentence plus the
 * exception type, which is enough for support to recognise it.
 */
export type MailboxHealth =
  | { kind: 'ok' }
  | { kind: 'revoked' | 'error'; message: string; detail: string };

type Channel = Pick<
  MailboxChannelOut,
  'provider' | 'status' | 'connected' | 'last_error'
>;

const REVOKED_PATTERN = /grant was revoked|invalid_grant/i;
const FORBIDDEN_PATTERN = /\b403\b|forbidden/i;
/** The exception type at the head of `last_error`, e.g. `HTTPStatusError: ...`. */
const ERROR_TYPE_PATTERN = /^([A-Za-z_][\w.]*)\s*:/;

/** `last_error` with every query string removed, for a tooltip. */
export function withoutQueryStrings(raw: string): string {
  return raw.replace(/\?[^\s'"]*/g, '');
}

export function mailboxHealth(channel: Channel): MailboxHealth {
  const name = channel.provider === 'GOOGLE' ? 'Google' : 'Microsoft';
  const raw = channel.last_error ?? '';
  const detail = withoutQueryStrings(raw);

  if (channel.status === 'REVOKED' || REVOKED_PATTERN.test(raw)) {
    return {
      kind: 'revoked',
      message: `${name} revoked access. Reconnect to resume.`,
      detail,
    };
  }
  if (!channel.connected) {
    // A row with no usable credential, whatever its status claims: the token
    // was cleared, or it no longer decrypts after a key rotation.
    return {
      kind: 'revoked',
      message: 'This mailbox is no longer authorised. Reconnect to resume.',
      detail,
    };
  }
  if (channel.status !== 'ERROR') {
    return { kind: 'ok' };
  }
  if (FORBIDDEN_PATTERN.test(raw)) {
    return {
      kind: 'error',
      message:
        channel.provider === 'GOOGLE'
          ? 'Google refused access to this mailbox. Reconnect, and check the Gmail API is enabled.'
          : 'Microsoft refused access to this mailbox. Reconnect to resume.',
      detail,
    };
  }
  const type = ERROR_TYPE_PATTERN.exec(raw)?.[1];
  return {
    kind: 'error',
    message: type
      ? `The last sync failed (${type}). Reconnect if it keeps failing.`
      : 'The last sync failed. Reconnect if it keeps failing.',
    detail,
  };
}
